import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface PaymentConfirmModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	stationName: string;
	amountHintText: string;
	amountValue: string;
	onAmountChange: (value: string) => void;
	isSubmitting?: boolean;
	partnerCity?: string | null;
	partnerCountry?: string | null;
	ownerPhone?: string | null;
	bankName?: string | null;
	bankAccountNumber?: string | null;
	bankAccountHolder?: string | null;
	/** When true, payment amount is fixed (e.g. bulk payout sum) */
	disableAmountEditor?: boolean;
	subtitle?: string | null;
	confirmButtonLabel?: string;
	/** Optional admin note (not required). */
	noteValue?: string;
	onNoteChange?: (value: string) => void;
	/** Có giá trị ⇒ ô ghi chú thành "Reason (required)", nút xác nhận khoá tới khi đủ số ký tự. */
	noteRequiredMin?: number;
}

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(17, 24, 39, 0.55);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000000;
	padding: 20px;
`;

const ModalContainer = styled.div`
	width: 520px;
	max-width: 95vw;
	background: #ffffff;
	border-radius: 14px;
	box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);
	padding: 26px 28px 22px;
`;

const ModalTitle = styled.h3`
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: #0f172a;
`;

const ModalMessage = styled.p`
	margin: 14px 0 0;
	font-size: 16px;
	line-height: 1.6;
	color: #334155;
`;

const AmountEditor = styled.div`
	margin-top: 14px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const AmountLabel = styled.label`
	font-size: 14px;
	font-weight: 600;
	color: #334155;
`;

const AmountInput = styled.input`
	width: 100%;
	border: 1px solid #cbd5e1;
	border-radius: 10px;
	padding: 12px 14px;
	font-size: 16px;
	font-weight: 600;
	color: #0f172a;

	&:focus {
		outline: none;
		border-color: #0b74c6;
		box-shadow: 0 0 0 3px rgba(11, 116, 198, 0.15);
	}
`;

const AmountHint = styled.div`
	font-size: 13px;
	color: #64748b;
`;

const NoteTextarea = styled.textarea`
	width: 100%;
	min-height: 88px;
	margin-top: 4px;
	border: 1px solid #cbd5e1;
	border-radius: 10px;
	padding: 12px 14px;
	font-size: 14px;
	color: #0f172a;
	font-family: inherit;
	resize: vertical;
	box-sizing: border-box;

	&:focus {
		outline: none;
		border-color: #0b74c6;
		box-shadow: 0 0 0 3px rgba(11, 116, 198, 0.15);
	}
`;

const NoteOptionalHint = styled.div`
	font-size: 12px;
	color: #94a3b8;
	margin-top: 4px;
`;

const BankInfoCard = styled.div`
	margin-top: 18px;
	padding: 14px 16px;
	border-radius: 12px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const WarningBox = styled.div`
	margin-top: 18px;
	padding: 12px 14px;
	border-radius: 12px;
	background: #fff7ed;
	border: 1px solid #fdba74;
	font-size: 14px;
	line-height: 1.5;
	color: #9a3412;
`;

const BankInfoTitle = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: #0f172a;
`;

const BankInfoGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const BankInfoItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const BankInfoLabel = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #64748b;
	text-transform: uppercase;
`;

const BankInfoValue = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #0f172a;
	word-break: break-word;
`;

const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 24px;
`;

const ActionButton = styled.button<{ variant: 'cancel' | 'confirm' }>`
	border-radius: 10px;
	border: none;
	padding: 11px 20px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	opacity: ${(props) => (props.disabled ? 0.7 : 1)};
	pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

	${(props) =>
		props.variant === 'cancel'
			? `
		background: #e2e8f0;
		color: #334155;
		&:hover {
			background: #cbd5e1;
		}
	`
			: `
		background: #0b74c6;
		color: #ffffff;
		&:hover {
			background: #095f9f;
		}
	`}
`;

export default function PaymentConfirmModal({
	isOpen,
	onClose,
	onConfirm,
	stationName,
	amountHintText,
	amountValue,
	onAmountChange,
	isSubmitting = false,
	partnerCity,
	partnerCountry,
	ownerPhone,
	bankName,
	bankAccountNumber,
	bankAccountHolder,
	disableAmountEditor = false,
	subtitle = null,
	confirmButtonLabel,
	noteValue = "",
	onNoteChange,
	noteRequiredMin,
}: PaymentConfirmModalProps) {
	if (!isOpen) return null;

	const parsedAmount = parseFloat(`${amountValue}`.replace(/,/g, ""));
	const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;
	const noteValid = !noteRequiredMin || noteValue.trim().length >= noteRequiredMin;

	const missingRequiredFields = [
		!`${partnerCity || ""}`.trim() ? "City" : null,
		!`${partnerCountry || ""}`.trim() ? "Country" : null,
		!`${bankName || ""}`.trim() ? "Bank name" : null,
		!`${bankAccountNumber || ""}`.trim() ? "Bank account number" : null,
		!`${bankAccountHolder || ""}`.trim() ? "Account holder name" : null,
	].filter(Boolean) as string[];

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget && !isSubmitting) {
			onClose();
		}
	};

	const modalContent = (
		<ModalOverlay onClick={handleOverlayClick}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<ModalTitle>Confirm payment</ModalTitle>
				{subtitle ? (
					<ModalMessage style={{ marginTop: "8px", fontSize: "14px", color: "#475569" }}>{subtitle}</ModalMessage>
				) : null}
				<ModalMessage>
					You are about to process a payment for <strong>{stationName}</strong>.
				</ModalMessage>
				<AmountEditor>
					<AmountLabel>Payment amount</AmountLabel>
					<AmountInput
						type="text"
						value={amountValue}
						onChange={(e) => onAmountChange(e.target.value)}
						disabled={isSubmitting || disableAmountEditor}
						readOnly={disableAmountEditor}
						placeholder="Enter payment amount"
					/>
					<AmountHint>Suggested amount: {amountHintText}</AmountHint>
				</AmountEditor>
				{onNoteChange ? (
					<AmountEditor style={{ marginTop: "10px" }}>
						<AmountLabel>{noteRequiredMin ? "Reason (required)" : "Note (optional)"}</AmountLabel>
						<NoteTextarea
							value={noteValue}
							onChange={(e) =>
								onNoteChange(e.target.value.slice(0, 500))
							}
							disabled={isSubmitting}
							placeholder={noteRequiredMin ? "Why is this paid outside Wise? e.g. LAK bank transfer, ref …" : "Add note if any"}
							maxLength={500}
							rows={3}
							aria-label={noteRequiredMin ? "Payment reason" : "Optional payment note"}
						/>
						<NoteOptionalHint>
							{noteRequiredMin
								? `Required — at least ${noteRequiredMin} characters, saved with the payment.`
								: "Optional — max 500 characters."}
						</NoteOptionalHint>
					</AmountEditor>
				) : null}
				{missingRequiredFields.length > 0 && (
					<WarningBox>
						<strong>Missing required fields:</strong> {missingRequiredFields.join(", ")}
					</WarningBox>
				)}
				<BankInfoCard>
					<BankInfoTitle>Partner bank information</BankInfoTitle>
					<BankInfoGrid>
						<BankInfoItem>
							<BankInfoLabel>City</BankInfoLabel>
							<BankInfoValue>{partnerCity || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Country</BankInfoLabel>
							<BankInfoValue>{partnerCountry || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Owner phone</BankInfoLabel>
							<BankInfoValue>{ownerPhone || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Bank name</BankInfoLabel>
							<BankInfoValue>{bankName || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Bank account number</BankInfoLabel>
							<BankInfoValue>{bankAccountNumber || "Not configured"}</BankInfoValue>
						</BankInfoItem>
						<BankInfoItem>
							<BankInfoLabel>Account holder name</BankInfoLabel>
							<BankInfoValue>{bankAccountHolder || "Not configured"}</BankInfoValue>
						</BankInfoItem>
					</BankInfoGrid>
				</BankInfoCard>
				<Footer>
					<ActionButton
						variant="cancel"
						onClick={onClose}
						disabled={isSubmitting}
					>
						Cancel
					</ActionButton>
					<ActionButton
						variant="confirm"
						onClick={onConfirm}
						disabled={isSubmitting || !amountValid || !noteValid}
					>
						{isSubmitting ? "Processing..." : confirmButtonLabel || "Confirm payment"}
					</ActionButton>
				</Footer>
			</ModalContainer>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
